import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ROUTES } from '@/app/router/routes';
import { DetailSection } from '@/components/ui/DetailSection';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { TimesheetIcon } from '@/components/icons';
import type { PortalMission } from '@/features/portal/types';
import { defaultMissionMonth, formatMissionDate, missionMonths } from './mission-periods';
import { formatMonth } from './timesheet-labels';
import { useMyTimesheets, useOpenTimesheet } from './useTimesheets';

export const TimesheetDeclaration = ({
  missions,
  initialMissionId = '',
}: {
  missions: PortalMission[];
  initialMissionId?: string;
}) => {
  const navigate = useNavigate();
  const [missionId, setMissionId] = useState(initialMissionId);
  const [month, setMonth] = useState('');
  const selected =
    missions.find((mission) => mission.id === missionId) ??
    (missions.length === 1 ? missions[0] : undefined);
  const months = selected ? missionMonths(selected) : [];
  const periodMonth = months.includes(month) ? month : defaultMissionMonth(months);
  const open = useOpenTimesheet();
  const existing = useMyTimesheets(
    { missionId: selected?.id ?? '', periodMonth },
    Boolean(selected && periodMonth),
  );
  const ready = Boolean(selected && periodMonth && existing.isSuccess);
  const record = existing.data?.items.find(
    (item) => item.mission.id === selected?.id && item.periodMonth === periodMonth,
  );
  return (
    <DetailSection title="Déclarer un CRA" icon={<TimesheetIcon />}>
      <div className="space-y-4">
        <p className="text-ink-500 text-xs leading-6">
          Un compte rendu par mission et par mois. Choisissez la mission concernée.
        </p>
        <div className="grid items-end gap-4 md:grid-cols-[minmax(0,1fr)_minmax(180px,240px)_auto]">
          <Select
            label="Mission"
            placeholder="Choisissez une mission"
            value={selected?.id ?? ''}
            options={missions.map((mission) => ({
              value: mission.id,
              label: `${mission.title || mission.projectName}${mission.clientName ? ` · ${mission.clientName}` : ''}${mission.reference ? ` · ${mission.reference}` : ''}`,
            }))}
            disabled={open.isPending}
            onChange={(event) => {
              setMissionId(event.target.value);
              setMonth('');
              open.reset();
            }}
          />
          <Select
            label="Mois à déclarer"
            placeholder={selected ? 'Aucun mois disponible' : 'Choisissez d’abord une mission'}
            value={periodMonth}
            disabled={!months.length || open.isPending}
            options={months.map((value) => ({ value, label: formatMonth(value) }))}
            onChange={(event) => {
              setMonth(event.target.value);
              open.reset();
            }}
          />
          <Button
            disabled={!ready || open.isPending}
            loading={open.isPending}
            loadingLabel="Ouverture…"
            onClick={() => {
              if (!selected || !ready) return;
              if (record) {
                void navigate(ROUTES.timesheet(record.id));
                return;
              }
              open.mutate(
                { missionId: selected.id, periodMonth },
                {
                  onSuccess: (sheet) => {
                    void navigate(ROUTES.timesheet(sheet.id));
                  },
                },
              );
            }}
          >
            <TimesheetIcon />
            {record ? 'Ouvrir le CRA' : 'Déclarer ce mois'}
          </Button>
        </div>
        {selected && (
          <p className="text-ink-500 text-xs leading-relaxed">
            Mission du <strong>{formatMissionDate(selected.startDate)}</strong>
            {selected.endDate ? (
              <>
                {' '}
                au <strong>{formatMissionDate(selected.endDate)}</strong>
              </>
            ) : (
              ' · sans date de fin'
            )}
            .{' '}
            {months.length
              ? 'Seuls les mois compris dans cette période sont proposés. Les journées hors mission restent désactivées.'
              : 'Vous pourrez déclarer votre activité à partir du début de la mission.'}{' '}
            <Link to={ROUTES.mission(selected.id)} className="text-accent-700 underline">
              Voir la mission
            </Link>
          </p>
        )}
        {record && (
          <p className="text-ink-500 text-xs">
            Un CRA existe déjà pour cette mission et ce mois : reprenez-le directement.
          </p>
        )}
        {existing.isError && selected && periodMonth && (
          <p role="alert" className="text-sm text-red-800">
            Impossible de vérifier les CRA existants.{' '}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                void existing.refetch();
              }}
            >
              Réessayer
            </Button>
          </p>
        )}
        {open.error && (
          <p role="alert" className="text-sm text-red-800">
            {open.error.message}
          </p>
        )}
      </div>
    </DetailSection>
  );
};
